import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { resolveApiBase } from '../utils/httpClient';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const isAdminShell = (user) =>
  Boolean(
    user &&
      (user.is_staff ||
        user.is_superuser ||
        user.user_type === 'admin')
  );

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Create axios instance with base URL
  const api = axios.create({
    baseURL: resolveApiBase(),
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // Add token to requests
  api.interceptors.request.use((config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  // Handle token refresh
  api.interceptors.response.use(
    (response) => response,
    async (error) => {
      if (error.response?.status === 401) {
        const refreshToken = localStorage.getItem('refresh_token');
        if (refreshToken) {
          try {
            const response = await axios.post(`${api.defaults.baseURL}/api/token/refresh/`, {
              refresh: refreshToken,
            });
            localStorage.setItem('access_token', response.data.access);
            error.config.headers.Authorization = `Bearer ${response.data.access}`;
            return api.request(error.config);
          } catch (refreshError) {
            logout();
          }
        }
      }
      return Promise.reject(error);
    }
  );

  const enrichAdminRole = async (baseUser) => {
    if (!isAdminShell(baseUser)) return baseUser;
    try {
      const { data } = await api.get('/api/admin/me/');
      return {
        ...baseUser,
        admin_role: data.role,
        is_sales_limited: Boolean(data.is_sales_limited),
        can_manage_partners: Boolean(data.can_manage_partners),
        admin_home_path: data.home_path || '/admin/dashboard',
        admin_permissions: data.permissions_list || [],
      };
    } catch (_) {
      return baseUser;
    }
  };

  const login = async (username, password) => {
    try {
      setError(null);
      const response = await api.post('/api/auth/login/', { username, password });
      const { tokens, user: rawUser } = response.data;

      localStorage.setItem('access_token', tokens.access);
      localStorage.setItem('refresh_token', tokens.refresh);
      const nextUser = await enrichAdminRole(rawUser);
      setUser(nextUser);
      return { success: true, user: nextUser };
    } catch (error) {
      const status = error.response?.status;
      let errorMessage = error.response?.data?.error || 'Login failed';
      if (!error.response || status === 502 || status === 503 || status === 504) {
        errorMessage =
          'Auth service is unavailable (gateway 502/down). Wait a few seconds and retry.';
      }
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const register = async (userData) => {
    try {
      setError(null);
      const response = await api.post('/api/auth/register/', userData);
      // Email verification gate — tokens issued after verify_email, not at register
      if (response.data?.tokens?.access) {
        localStorage.setItem('access_token', response.data.tokens.access);
        localStorage.setItem('refresh_token', response.data.tokens.refresh);
        if (response.data.user) setUser(response.data.user);
      }
      return {
        success: true,
        requiresEmailVerification: Boolean(response.data?.requires_email_verification ?? true),
        message: response.data?.message,
        userType: response.data?.user_type,
        email: response.data?.email,
      };
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Registration failed';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const logout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    setUser(null);
    setError(null);
  };

  const verifyEmail = async (token) => {
    try {
      setError(null);
      const response = await api.post('/api/auth/verify-email/', { token });
      const access = response.data?.access_token || response.data?.tokens?.access;
      const refresh = response.data?.refresh_token || response.data?.tokens?.refresh;
      if (access) localStorage.setItem('access_token', access);
      if (refresh) localStorage.setItem('refresh_token', refresh);
      let nextUser = response.data?.user || null;
      if (access) {
        try {
          const profileRes = await api.get('/api/auth/profile/');
          nextUser = profileRes.data?.user || profileRes.data || nextUser;
        } catch (_) {
          // Token is enough to continue; profile may load on next navigation.
        }
      }
      if (nextUser) setUser(nextUser);
      return {
        success: true,
        message: response.data?.message || 'Email verified successfully.',
        user: nextUser,
      };
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Email verification failed';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const claimInvite = async ({ uid, token, password, confirmPassword }) => {
    try {
      setError(null);
      const { data } = await api.post('/api/auth/set-password/', {
        uid,
        token,
        password,
        confirm_password: confirmPassword,
      });
      localStorage.setItem('access_token', data.tokens.access);
      localStorage.setItem('refresh_token', data.tokens.refresh);
      setUser(data.user);
      return { success: true, user: data.user, message: data.message };
    } catch (error) {
      return {
        success: false,
        code: error.response?.data?.code,
        error: error.response?.data?.error || 'Could not set your password. Try again.',
      };
    }
  };

  const updateProfile = async (profileData) => {
    try {
      setError(null);
      const response = await api.put('/api/auth/profile/', profileData);
      setUser(response.data.user);
      return { success: true };
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Profile update failed';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const getProfile = async () => {
    try {
      const response = await api.get('/api/auth/profile/');
      const nextUser = await enrichAdminRole(response.data.user);
      setUser(nextUser);
      return nextUser;
    } catch (error) {
      console.error('Failed to get profile:', error);
      return null;
    }
  };

  // Check if user is authenticated on app load
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('access_token');
      if (token) {
        try {
          await getProfile();
        } catch (error) {
          console.log('Auth check failed, logging out:', error);
          logout();
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, []);

  const value = {
    user,
    loading,
    error,
    login,
    register,
    logout,
    verifyEmail,
    claimInvite,
    updateProfile,
    getProfile,
    api,
    isAuthenticated: !!user,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 